import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, CheckCircle, User, Phone, Mail, MapPin, Briefcase,
  MessageCircle, X, Sparkles, ShieldCheck, Copy,
} from "lucide-react";
import { API_URL, LOGO_URL, BRAND_NAME, axios } from "../constants";

/**
 * Public Decorator Registration Portal (/decorator-registration)
 *
 * Mobile-first single-page form that collects decorator applications
 * into the new `decorator_registrations` collection. NEVER auto-creates
 * a vendor profile — an admin must explicitly convert an approved
 * application from the Decorator Registrations tab.
 */

const PINCODE_RE = /^\d{6}$/;
const PHONE_RE = /^[6-9]\d{9}$/;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const InputField = ({ icon: Icon, label, hint, required, children }) => (
  <div>
    <label className="text-sm font-semibold text-[#1E1B4B] mb-1 flex items-center gap-1.5">
      {Icon && <Icon size={13} className="text-[#E83E8C]" />} {label}
      {required && <span className="text-red-400">*</span>}
    </label>
    {children}
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </div>
);

export default function DecoratorRegistration() {
  const [form, setForm] = useState({
    name: "", phone: "", whatsapp_phone: "", email: "",
    experience_years: "", consent: false, website: "",
  });
  const [pincodeInput, setPincodeInput] = useState("");
  const [pincodes, setPincodes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null); // { application_id }
  const [copied, setCopied] = useState(false);
  const submitLockRef = useRef(false);

  // Open Graph / social metadata — set on mount, restored on unmount
  useEffect(() => {
    const prev = { title: document.title };
    document.title = `Become a DoozyGo® Partner Decorator — Register`;
    const metas = {
      "description": "Join DoozyGo® as a partner decorator. Share your details, get verified, and start receiving bookings in your area.",
      "og:title": "Become a DoozyGo® Partner Decorator",
      "og:description": "Apply to decorate for India's fastest-growing house-party platform. Verified bookings, on-time payouts.",
      "og:image": LOGO_URL,
      "og:type": "website",
      "twitter:card": "summary_large_image",
    };
    const addedTags = [];
    Object.entries(metas).forEach(([name, content]) => {
      const attr = name.startsWith("og:") || name.startsWith("twitter:") ? "property" : "name";
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
        addedTags.push(tag);
      }
      tag.setAttribute("content", content);
    });
    return () => {
      document.title = prev.title;
      addedTags.forEach(t => t.remove());
    };
  }, []);

  const handleInput = (field, value) => {
    if (field === "phone" || field === "whatsapp_phone") {
      value = String(value || "").replace(/\D/g, "").slice(0, 10);
    }
    if (field === "experience_years") {
      value = String(value || "").replace(/\D/g, "").slice(0, 2);
    }
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addPincode = (raw) => {
    const code = String(raw || "").replace(/\D/g, "").slice(0, 6);
    if (!PINCODE_RE.test(code)) {
      setError("Enter a 6-digit pincode.");
      return;
    }
    if (pincodes.includes(code)) {
      setPincodeInput("");
      return;
    }
    if (pincodes.length >= 10) {
      setError("You can add up to 10 pincodes.");
      return;
    }
    setPincodes([...pincodes, code]);
    setPincodeInput("");
    setError("");
  };

  const handlePincodeKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addPincode(pincodeInput);
    } else if (e.key === "Backspace" && !pincodeInput && pincodes.length) {
      setPincodes(pincodes.slice(0, -1));
    }
  };

  const removePincode = (p) => setPincodes(pincodes.filter(x => x !== p));

  const validate = () => {
    if (form.name.trim().length < 2) return "Please enter your full name.";
    if (!PHONE_RE.test(form.phone)) return "Enter a valid 10-digit Indian mobile number.";
    if (form.whatsapp_phone && !PHONE_RE.test(form.whatsapp_phone))
      return "Enter a valid 10-digit WhatsApp number or leave it blank.";
    if (form.email && !EMAIL_RE.test(form.email))
      return "Enter a valid email address or leave it blank.";
    if (pincodes.length === 0) return "Add at least one 6-digit service pincode.";
    const exp = parseInt(form.experience_years || "0", 10);
    if (isNaN(exp) || exp < 0 || exp > 60) return "Years of experience must be between 0 and 60.";
    if (!form.consent) return "Please tick the consent checkbox to continue.";
    return null;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (submitLockRef.current || submitting) return;
    const validation = validate();
    if (validation) { setError(validation); return; }
    setError("");
    submitLockRef.current = true;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/decorator-registrations`, {
        name: form.name.trim(),
        phone: form.phone,
        whatsapp_phone: form.whatsapp_phone || "",
        email: form.email.trim(),
        pincodes,
        experience_years: parseInt(form.experience_years || "0", 10),
        consent: form.consent,
        website: form.website, // honeypot — always empty for humans
      });
      setSuccess({ application_id: res.data.application_id });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
      // Keep the lock until success renders; release on failure so retry works
      setTimeout(() => { submitLockRef.current = false; }, 600);
    }
  };

  const copyAppId = async () => {
    if (!success?.application_id) return;
    try {
      await navigator.clipboard.writeText(success.application_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFF5F8] via-white to-[#FFF9F0]">
        <TopBar />
        <main className="max-w-xl mx-auto px-4 pt-10 pb-20">
          <div className="bg-white rounded-3xl shadow-xl border border-pink-100 p-8 text-center" data-testid="decorator-registration-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] flex items-center justify-center mb-5 shadow-lg">
              <CheckCircle className="text-white" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-[#1E1B4B]">You're on the list!</h1>
            <p className="text-sm text-[#64748B] mt-2">
              Thank you for applying to decorate with {BRAND_NAME}. Our Operations team will review your details and reach out shortly.
            </p>
            <div className="mt-6 bg-gradient-to-br from-pink-50 to-amber-50 rounded-2xl p-5 border border-pink-100">
              <p className="text-xs font-semibold text-[#1E1B4B] uppercase tracking-wider">Your Application ID</p>
              <div className="flex items-center justify-center gap-2 mt-2">
                <span className="text-2xl font-bold text-[#E83E8C] tracking-wider" data-testid="decorator-application-id">
                  {success.application_id}
                </span>
                <button
                  onClick={copyAppId}
                  className="p-2 rounded-lg hover:bg-white transition"
                  data-testid="decorator-copy-application-id"
                  aria-label="Copy application ID"
                >
                  <Copy size={16} className="text-[#1E1B4B]" />
                </button>
              </div>
              {copied && <p className="text-[11px] text-green-600 mt-1">Copied!</p>}
              <p className="text-[11px] text-gray-500 mt-3">
                Save this ID. We'll reference it when we contact you.
              </p>
            </div>
            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-[#64748B]">
              <ShieldCheck size={14} className="text-green-500" />
              Your details are private and used only to process this application.
            </div>
            <Link
              to="/"
              className="inline-block mt-7 px-6 py-3 rounded-xl bg-[#1E1B4B] text-white text-sm font-semibold hover:bg-[#2a2560] transition"
              data-testid="decorator-success-home-link"
            >
              Back to {BRAND_NAME}
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF5F8] via-white to-[#FFF9F0]">
      <TopBar />
      <main className="max-w-2xl mx-auto px-4 pt-6 pb-16" data-testid="decorator-registration-page">
        <div className="text-center mb-6">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pink-100 text-[#E83E8C] text-xs font-semibold">
            <Sparkles size={12} /> Partner with {BRAND_NAME}
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1E1B4B] mt-3 leading-tight">
            Become a Partner Decorator
          </h1>
          <p className="text-sm text-[#64748B] mt-2 max-w-md mx-auto">
            Share a few details and our Operations team will get in touch. Verified bookings, on-time payouts, zero chasing.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-3xl shadow-xl border border-pink-100 p-6 sm:p-8 space-y-5">
          {/* Honeypot — hidden from humans, bots fill it */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(e) => handleInput("website", e.target.value)}
            className="hidden"
            aria-hidden="true"
          />

          <InputField icon={User} label="Full Name" required>
            <input
              value={form.name}
              onChange={(e) => handleInput("name", e.target.value)}
              placeholder="e.g. Ravi Kumar"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm"
              data-testid="decorator-input-name"
              maxLength={80}
              required
            />
          </InputField>

          <InputField icon={Phone} label="Mobile Number" hint="We'll call or WhatsApp you on this number." required>
            <div className="flex items-center gap-2">
              <span className="px-3 py-2.5 rounded-xl bg-gray-100 text-sm text-[#1E1B4B] font-semibold select-none">+91</span>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => handleInput("phone", e.target.value)}
                placeholder="10-digit mobile"
                className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm font-mono tracking-wide"
                data-testid="decorator-input-phone"
                inputMode="numeric"
                required
              />
            </div>
          </InputField>

          <InputField icon={MessageCircle} label="WhatsApp Number" hint="Optional — only if different from your mobile.">
            <div className="flex items-center gap-2">
              <span className="px-3 py-2.5 rounded-xl bg-gray-100 text-sm text-[#1E1B4B] font-semibold select-none">+91</span>
              <input
                type="tel"
                value={form.whatsapp_phone}
                onChange={(e) => handleInput("whatsapp_phone", e.target.value)}
                placeholder="10-digit WhatsApp (optional)"
                className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm font-mono tracking-wide"
                data-testid="decorator-input-whatsapp"
                inputMode="numeric"
              />
            </div>
          </InputField>

          <InputField icon={Mail} label="Email" hint="Optional — we'll send booking receipts here.">
            <input
              type="email"
              value={form.email}
              onChange={(e) => handleInput("email", e.target.value)}
              placeholder="you@email.com"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm"
              data-testid="decorator-input-email"
              maxLength={120}
            />
          </InputField>

          <InputField icon={MapPin} label="Service Pincodes" hint="Type a 6-digit code and press Enter. Up to 10." required>
            <div className="flex flex-wrap items-center gap-2 px-2 py-2 rounded-xl border border-gray-200 focus-within:border-[#E83E8C] focus-within:ring-2 focus-within:ring-[#E83E8C]/20 min-h-[46px]" data-testid="decorator-pincode-chips">
              {pincodes.map((p) => (
                <span key={p} className="inline-flex items-center gap-1 bg-pink-50 text-[#E83E8C] text-xs font-semibold px-2.5 py-1 rounded-full border border-pink-100" data-testid={`decorator-pincode-chip-${p}`}>
                  {p}
                  <button
                    type="button"
                    onClick={() => removePincode(p)}
                    className="hover:text-pink-800"
                    aria-label={`Remove ${p}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              <input
                value={pincodeInput}
                onChange={(e) => setPincodeInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={handlePincodeKey}
                onBlur={() => pincodeInput && addPincode(pincodeInput)}
                placeholder={pincodes.length ? "" : "e.g. 400001"}
                className="flex-1 min-w-[100px] px-2 py-1 text-sm outline-none bg-transparent font-mono"
                data-testid="decorator-input-pincode"
                inputMode="numeric"
              />
            </div>
          </InputField>

          <InputField icon={Briefcase} label="Years of Decorating Experience" required>
            <input
              type="text"
              value={form.experience_years}
              onChange={(e) => handleInput("experience_years", e.target.value)}
              placeholder="e.g. 3"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm"
              data-testid="decorator-input-experience"
              inputMode="numeric"
              required
            />
          </InputField>

          <label className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-100 cursor-pointer" data-testid="decorator-consent-block">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => setForm(prev => ({ ...prev, consent: e.target.checked }))}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#E83E8C] focus:ring-[#E83E8C]"
              data-testid="decorator-input-consent"
              required
            />
            <span className="text-xs text-[#1E1B4B] leading-relaxed">
              I agree to be contacted by {BRAND_NAME} on the mobile / WhatsApp / email provided above to discuss my application and partnership opportunities.
            </span>
          </label>

          {error && (
            <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm" data-testid="decorator-form-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white font-bold text-sm shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed transition"
            data-testid="decorator-submit-btn"
          >
            {submitting ? "Submitting…" : "Submit Application"}
          </button>

          <p className="text-[11px] text-gray-500 text-center">
            This application does not grant vendor access. An authorised {BRAND_NAME} admin will review and contact you.
          </p>
        </form>
      </main>
    </div>
  );
}

const TopBar = () => (
  <header className="sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-pink-100">
    <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2 text-[#1E1B4B]" data-testid="decorator-back-home">
        <ChevronLeft size={18} />
        <img src={LOGO_URL} alt={BRAND_NAME} className="h-7 w-auto" />
      </Link>
      <span className="text-xs font-semibold text-[#E83E8C]">Decorator Registration</span>
    </div>
  </header>
);
